import React, { useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';

import { Titles } from '../../components/Titles/Titles';

import { OnboardingFlowContext } from '../../OnboardingFlowContext';
import { defaultCaregiverFlow, defaultPatientFlow } from '../../userFlows';
import { OnboardingParams } from '../../types';

import { SelectButton } from '@/components/Button';
import { isSpark as isSparkHelper } from '@/utils/productHelpers';

export const WhoAreYouEnrolling: React.FC = () => {
  const {
    next, updateFormData, onboardingFormData, setSteps, resetFormData,
  } = useContext(OnboardingFlowContext);
  const { product, slug } = useParams<OnboardingParams>();
  const history = useHistory();
  const { t } = useTranslation();

  const handleSelect = (kind: 'patient' | 'caregiver') => {
    resetFormData();
    updateFormData({ personEnrolling: kind });

    const flow = kind === 'caregiver' ? defaultCaregiverFlow : defaultPatientFlow;
    setSteps(flow);

    next();
  };

  const isPatient = onboardingFormData.personEnrolling === 'patient';
  const isCaregiver = onboardingFormData.personEnrolling === 'caregiver';
  const theme = useTheme();
  const choiceBaseStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 100,
    padding: '12px 16px',
    textTransform: 'none',
    fontWeight: 600,
    justifyContent: 'center',
    borderWidth: isPatient ? 2 : 1.5,
  };

  return (
    <div style={{ marginBottom: '40px' }}>
      <Titles
        title={t(`coverage.${product}.person_enrolling.title`)}
        subtitle={t(`coverage.${product}.person_enrolling.subtitle`)}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 12,
        }}
      >
        <SelectButton
          aria-pressed={isPatient}
          data-testid="myself-button"
          style={{
            ...choiceBaseStyle,
            borderWidth: isPatient ? 2 : 1.5,
          }}
          onClick={() => {
            if (isSparkHelper(product)) {
              history.replace(`/activate/${product}/${slug}?autoSelect=myself`);
              return;
            }
            handleSelect('patient');
          }}
        >
          {t(`coverage.${product}.person_enrolling.myself`)}
        </SelectButton>
        <SelectButton
          aria-pressed={isCaregiver}
          data-testid="dependent-button"
          style={{
            ...choiceBaseStyle,
            borderWidth: isCaregiver ? 2 : 1.5,
          }}
          onClick={() => handleSelect('caregiver')}
        >
          {t(`coverage.${product}.person_enrolling.dependent`)}
        </SelectButton>
      </div>
    </div>
  );
};
